/**
 * Navigation guards for native deep links — the reason `/l/mHDNQ7dt` reloaded
 * forever in the app, and the reason it cannot happen again on any path.
 *
 * Three layers, all pure and unit-tested in `deep-link-navigation.test.ts`:
 *
 * 1. **Launch dedupe** (`shouldHandleLaunchUrl`). Guards the `getLaunchUrl()`
 *    FALLBACK only — see the hook for why `appUrlOpen` is the primary path.
 *    `getLaunchUrl()` returns the OS cold-start URL on EVERY document reload,
 *    not the WebView's current location. A `/r/`, `/c/` or `/s/` arrival does
 *    a document navigation (Route Handlers cannot be client-navigated), which
 *    remounts the root provider, which re-read the same launch URL and
 *    navigated again: forever. `appUrlOpen` has no such failure mode — it
 *    fires once per actual open — which is the other reason to prefer it. The
 *    guard records each handled URL in `sessionStorage` (survives the reload,
 *    dies with the session — unlike `localStorage`, which would over-persist
 *    across app kills) plus an in-memory set for when storage throws.
 *
 * 2. **In-flight dedupe** (`shouldSkipDocumentNavigation`). A cold start can be
 *    delivered by BOTH `appUrlOpen` and the `getLaunchUrl()` fallback, and
 *    StrictMode double-invokes effects in dev. Same target twice within the
 *    window collapses to one navigation — one wasted reload at worst.
 *
 * 3. **Storm breaker** (`recordDocumentNavigation`). Last resort: if document
 *    navigations to redirect handlers ever exceed `NAV_STORM_MAX` inside
 *    `NAV_STORM_WINDOW_MS`, the caller must refuse and safe-harbor to `/`
 *    instead. Legit flows do at most ~2; the breaker only trips in a true
 *    loop, and guarantees termination no matter what unknown bug appears.
 */

export const LAUNCH_HANDLED_KEY = "hiive:handled-launch-url";

/** Same target twice inside this window is one navigation, not two. */
export const DOCUMENT_NAV_DEDUPE_MS = 2000;

/** More than this many redirect navigations inside the window is a loop. */
export const NAV_STORM_MAX = 8;
export const NAV_STORM_WINDOW_MS = 10_000;

export function normalizeInternalPath(value: string): string {
  const path = value.startsWith("/") ? value : `/${value}`;
  return path.replace(/\/+$/, "") || "/";
}

/** Minimal surface so tests can inject fakes; defaults to sessionStorage. */
export type LaunchUrlStorage = Pick<Storage, "getItem" | "setItem">;

function defaultStorage(): LaunchUrlStorage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage ?? null;
  } catch {
    return null;
  }
}

const handledLaunchUrlsMemory = new Set<string>();

/**
 * Record a launch URL as handled WITHOUT gating on it.
 *
 * `appUrlOpen` must call this rather than `shouldHandleLaunchUrl`. It has to
 * record, because a `/r/`, `/c/` or `/s/` arrival does a document navigation
 * and the next document's `getLaunchUrl()` fallback still reads the same URL
 * back from native state — unrecorded, it would follow the link a second time.
 * But it must not gate, because `appUrlOpen` fires once per ACTUAL open: the
 * same link tapped twice while the app runs is two real opens, and refusing
 * the second because it matches the first would drop it.
 */
export function markLaunchUrlHandled(
  rawUrl: string,
  storage: LaunchUrlStorage | null = defaultStorage(),
): void {
  handledLaunchUrlsMemory.add(rawUrl);
  try {
    storage?.setItem(LAUNCH_HANDLED_KEY, rawUrl);
  } catch {
    // Storage unavailable — see shouldHandleLaunchUrl.
  }
}

export function shouldHandleLaunchUrl(
  rawUrl: string,
  storage: LaunchUrlStorage | null = defaultStorage(),
): boolean {
  if (handledLaunchUrlsMemory.has(rawUrl)) return false;
  if (storage) {
    try {
      if (storage.getItem(LAUNCH_HANDLED_KEY) === rawUrl) {
        handledLaunchUrlsMemory.add(rawUrl);
        return false;
      }
    } catch {
      // Storage unavailable — the memory set below is the whole guard.
      // It covers everything except a full document reload, which is exactly
      // the case sessionStorage exists for; both failing together means the
      // WebView itself is broken, and navigating once is still correct.
    }
  }
  markLaunchUrlHandled(rawUrl, storage);
  return true;
}

let lastDocumentNavTarget: string | null = null;
let lastDocumentNavAt = 0;

export function shouldSkipDocumentNavigation(
  normalizedTarget: string,
  now: number = Date.now(),
): boolean {
  if (
    lastDocumentNavTarget === normalizedTarget &&
    now - lastDocumentNavAt < DOCUMENT_NAV_DEDUPE_MS
  ) {
    return true;
  }
  lastDocumentNavTarget = normalizedTarget;
  lastDocumentNavAt = now;
  return false;
}

const documentNavTimes: number[] = [];

/**
 * Record a redirect-handler navigation. Returns true when the storm breaker
 * has tripped and the caller must NOT perform the navigation.
 */
export function recordDocumentNavigation(now: number = Date.now()): boolean {
  while (documentNavTimes.length > 0) {
    const head = documentNavTimes[0];
    if (head === undefined || now - head <= NAV_STORM_WINDOW_MS) break;
    documentNavTimes.shift();
  }
  documentNavTimes.push(now);
  return documentNavTimes.length > NAV_STORM_MAX;
}

/** Test-only reset for the module-level guard state. */
export function resetDeepLinkNavigationForTests(): void {
  handledLaunchUrlsMemory.clear();
  lastDocumentNavTarget = null;
  lastDocumentNavAt = 0;
  documentNavTimes.length = 0;
}

/**
 * The link the iOS shell already loaded as the WebView's first document
 * (HiiveBridgeViewController.installColdStartMarker). iOS cannot consume the
 * launch the way Android does, so the same link still reaches JS afterwards
 * as a retained `appUrlOpen` / notification tap.
 */
export type NativeColdStartMarker = { url: string; at: number };

export const NATIVE_COLD_START_CONSUMED_KEY =
  "hiive:native-cold-start-consumed";

/**
 * How long after the shell followed a link its echo can still arrive. The
 * retained event is flushed when the listener attaches, i.e. after
 * hydration; generous because a slow network delays exactly that.
 */
export const NATIVE_COLD_START_MARKER_TTL_MS = 30_000;

function readNativeColdStartMarker(): NativeColdStartMarker | null {
  if (typeof window === "undefined") return null;
  const marker = (window as unknown as { __hiiveNativeColdStartLink?: unknown })
    .__hiiveNativeColdStartLink;
  if (
    !marker ||
    typeof marker !== "object" ||
    typeof (marker as NativeColdStartMarker).url !== "string" ||
    typeof (marker as NativeColdStartMarker).at !== "number"
  ) {
    return null;
  }
  return marker as NativeColdStartMarker;
}

const consumedMarkersMemory = new Set<number>();

/**
 * True exactly once for the echo of a link the native shell already opened:
 * same raw URL, still fresh, and not claimed before in this WebView session.
 *
 * The marker is a WKUserScript, which persists for the WebView's lifetime —
 * so without the once-per-session record a later, genuine re-tap of the same
 * link would be swallowed after any document reload.
 */
export function claimNativeColdStartLink(
  rawUrl: string,
  marker: NativeColdStartMarker | null = readNativeColdStartMarker(),
  storage: LaunchUrlStorage | null = defaultStorage(),
  now: number = Date.now(),
): boolean {
  if (!marker || marker.url !== rawUrl) return false;
  if (
    now - marker.at > NATIVE_COLD_START_MARKER_TTL_MS ||
    now < marker.at - 5_000
  )
    return false;
  if (consumedMarkersMemory.has(marker.at)) return false;
  try {
    if (storage?.getItem(NATIVE_COLD_START_CONSUMED_KEY) === String(marker.at))
      return false;
    storage?.setItem(NATIVE_COLD_START_CONSUMED_KEY, String(marker.at));
  } catch {
    // Memory alone still guarantees once per document.
  }
  consumedMarkersMemory.add(marker.at);
  return true;
}

/** True when the link carries a command the page must still run. */
export function hasCommandParam(rawUrl: string): boolean {
  try {
    const base =
      typeof window !== "undefined"
        ? window.location.origin
        : "https://x.invalid";
    return new URL(rawUrl, base).searchParams.has("action");
  } catch {
    return false;
  }
}

/** Test-only reset for the cold-start marker guard. */
export function resetNativeColdStartForTests(): void {
  consumedMarkersMemory.clear();
}
